import React from 'react';
import { Heading } from '@abyss/web/ui/Heading';
import { GreyCta } from '../GreyCta';
import { V2Badge } from '@abyss/web/ui/Badge';
import { V2Button } from '@abyss/web/ui/Button';
import { V2Link } from '@abyss/web/ui/Link';
import { Popover } from '@abyss/web/ui/Popover';
import { Riders } from '@src/components/Riders';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { QuoteBox } from '@src/components/QuoteBox';

import classNames from 'classnames';

import styles from './plansection.module.css';

export const RegularListing = ({ item }) => {
  return (
    <div className={styles.tierWrap}>
      <div className={styles.tierItems}>
        {item.costs.map((col, index) => {
          return (
            <React.Fragment>
              {col.sectionBreak && (
                <div className={styles.isFull}>
                  <div className={styles.sectionBreakHeading}>
                    {col.sectionBreak}
                  </div>
                </div>
              )}
              <div
                className={classNames(
                  styles.tierItem,
                  col.type === 'full' ? styles.isFull : ''
                )}
              >
                {col.tier && (
                  <div className={styles.tierPill}>
                    <V2Badge variant="info">{col.tier}</V2Badge>
                  </div>
                )}

                <div className={styles.tierTitle}>{col.tierTitle}</div>

                {col.listings.map((listing, index) => {
                  return (
                    <div
                      dangerouslySetInnerHTML={{ __html: listing }}
                      className={styles.scripSection}
                    />
                  );
                })}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export const ScripListing = ({ item }) => {
  return (
    <div className={styles.tierWrap}>
      <div className={styles.tierItems}>
        {item.costs.map((col, index) => {
          return (
            <div className={styles.tierItem}>
              <div className={styles.tierPill}>
                <V2Badge variant="info">{col.tier}</V2Badge>
              </div>
              <div className={styles.tierTitle}>{col.tierTitle}</div>
              <div
                dangerouslySetInnerHTML={{ __html: col.top }}
                className={styles.scripSection}
              />
              <div
                dangerouslySetInnerHTML={{ __html: col.bottom }}
                className={styles.scripSection}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DocumentsListing = ({ item, type = 'reg' }) => {
  return (
    <div className={styles.docWrap}>
      <div className={styles.docItems}>
        {item.areas.map((doc, index) => {
          return (
            <div
              className={classNames(styles.docSection, {
                [styles.thin]: type === 'thin',
              })}
            >
              <div className={styles.tierTitle}>{doc.title}</div>
              {doc.items.map((zed, index) => {
                return (
                  <div className={styles.docItem}>
                    {zed.icon === 'pdf' && (
                      <V2Button variant="text" className={styles.pdflink}>
                        <img src="/assets/icons/pdf.png" alt="pdf icon" />{' '}
                        {zed.item}
                      </V2Button>
                    )}
                    {zed.icon === 'link' && (
                      <V2Link
                        href="#"
                        openNewWindow
                        className={styles.external}
                      >
                        {zed.item}
                      </V2Link>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const PlanSection = ({ parent, items, layout, key }) => {
  return (
    <div
      className={classNames(
        styles.planCard,
        { [styles.ms]: parent === 'ms' },
        { [styles.isPushed]: layout }
      )}
      id={items.value}
      data-section
    >
      <div className={styles.planHead}>
        <div>
          <Heading.Level>
            <Heading className={styles.title}>
              <IconSymbol
                icon={items.icon}
                size="24px"
                color="$primary1"
                variant="outlined"
              />
              <span>{items.title}</span>
            </Heading>
          </Heading.Level>
        </div>
      </div>

      {/* Doctor Visits */}
      {items.value === 'doctor_visits' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <RegularListing item={items} />
            </div>
          </div>
          <section>
            <div>
              <GreyCta />
            </div>
          </section>
        </React.Fragment>
      )}

      {/* Emergency Care */}
      {items.value === 'urgent-emergency-care' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <RegularListing item={items} />
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Hospitalization */}
      {items.value === 'hospitalization' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <RegularListing item={items} />
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Outpatient */}
      {items.value === 'outpatient' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <RegularListing item={items} />
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Lab */}
      {items.value === 'lab' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <RegularListing item={items} />
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Wellness */}
      {items.value === 'benefits' && (
        <React.Fragment>
          <div className={classNames(styles.section, styles.glassesContainer)}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <div
                className={classNames(styles.blueBox, styles.blueBoxHearing)}
              >
                <div className={styles.blueBoxSubhead}>Dental discount</div>
                <div className={styles.blueBoxHearingWrap}>
                  <div className={styles.hearingImg}>
                    <img src="/assets/images/dental.png" alt="dental tools" />
                  </div>
                  <div className={styles.blueBoxCopy}>
                    <span>
                      UHC Dental provides access to over 50,000+ in-network
                      providers.
                    </span>
                    <span>
                      Discount available on a range of dental services
                      including: cleaning, exams, fillings, crowns, veneers,
                      implants and teeth whitening.
                    </span>
                  </div>
                </div>
              </div>

              <div
                className={classNames(styles.blueBox, styles.blueBoxGlasses)}
              >
                <div
                  className={classNames(
                    styles.blueBoxSubhead,
                    styles.subheadPush
                  )}
                >
                  Vision discount
                </div>
                <div className={styles.blueBoxGlassesWrap}>
                  <div className={styles.glassesImg}>
                    <img src="/assets/images/glasses.png" alt="toothbrush" />
                  </div>
                  <div className={styles.blueBoxCopy}>
                    <span>
                      Exclusive discounts on vision services, including exams,
                      contact lenses, and eyewear, from various providers.
                    </span>
                  </div>
                </div>
              </div>

              <div
                className={classNames(styles.blueBox, styles.blueBoxHearing)}
              >
                <div className={styles.blueBoxSubhead}>Hearing discount</div>
                <div className={styles.blueBoxTitle}>
                  $100 off per brand-name prescription <br /> $200 off per pair
                </div>
                <div className={styles.blueBoxHearingWrap}>
                  <div className={styles.hearingImg}>
                    <img src="/assets/images/hearing.png" alt="hearing" />
                  </div>
                  <div className={styles.blueBoxCopy}>
                    <span>
                      Additional $100 off per brand-name prescription hearing
                      aid on top of the already discounted AARP member rate
                      -meaning $200 off per pair.
                    </span>
                    <span>
                      No-cost hearing exam and consultation and expert support
                      from UnitedHealthcare Hearing’s nationwide network of
                      experienced hearing providers near you.
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: '15px' }}>
                  Access to Relate prescription hearing aids, UnitedHealthcare
                  Hearing’s private-label brand, for an affordable, high quality
                  option with a variety of technology options and helpful
                  features.
                </div>
              </div>

              <div
                className={classNames(styles.blueBox, styles.blueBoxGlasses)}
              >
                <div
                  className={classNames(
                    styles.blueBoxSubhead,
                    styles.subheadPush
                  )}
                >
                  Gym membership
                </div>
                <div className={styles.blueBoxTitle}>$0</div>
                <div className={styles.blueBoxGlassesWrap}>
                  <div className={styles.glassesImg}>
                    <img src="/assets/images/workout.png" alt="workout" />
                  </div>
                  <div className={styles.blueBoxCopy}>
                    <span>Gym membership at no additional cost to you.</span>
                    <span>
                      Access to a large network of national gyms and fitness
                      locations with a wide variety of fitness classes.
                    </span>
                    <span>
                      Access to thousands of on-demand workout videos and live
                      streaming fitness classes.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Optional riders */}
      {items.value === 'riders' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>
                {items.description}
                <div style={{ display: 'inline-block', marginLeft: '5px' }}>
                  <Popover content="Riders can provide additional coverage for your specific need(s) including: Coverage for Medicare Part A deductible, Part B excess coverage, preventive care, and Medicare Part B deductible.">
                    <IconSymbol icon="info" size="22px" variant="outlined" />
                  </Popover>
                </div>
              </div>
            </div>
            <div className={styles.body}>
              <Riders />
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Important resources */}
      {items.value === 'documents' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.intro}>
              <div className={styles.description}>{items.description}</div>
            </div>
            <div className={styles.body}>
              <DocumentsListing item={items} type={items.type} />
            </div>
            <div className={styles.docCta}>
              <div className={styles.docCtaWrap}>
                <div className={styles.docCtaContent}>
                  <div className={styles.docCtaMedia}>
                    <img
                      src="/assets/images/walking.jpg"
                      alt="walking friends"
                    />
                  </div>
                  <div>
                    <h3 className={styles.docCtaTitle}>
                      Get prescription drug coverage
                    </h3>
                    <div className={styles.docCtaSub}>
                      Add an AARP® MedicareRx Part D Plan from UnitedHealthcare
                      for prescription drug coverage
                    </div>
                    <div className={styles.docCtaCopy}>
                      Prescription drugs can be expensive, and Medicare Parts A
                      and B ("Original Medicare") may not provide the coverage
                      you need. Pairing an AARP MedicareRx Part D Plan from
                      UnitedHealthcare with a Medicare Supplement plan can help
                      protect you from unexpected medical and prescription drug
                      costs now or in the future.
                    </div>
                  </div>
                </div>
                <ul className={styles.docCtaList}>
                  <li>Low monthly premiums</li>
                  <li>$0 deductible</li>
                  <li>$0 copay for Tier 1 drugs</li>
                  <li>Stable, predictable insulin copays at $35</li>
                  <li>
                    Medication home delivery copays as low as $0 from OptumRx®
                  </li>
                  <li>Coverage for thousands of brand name generic drugs</li>
                </ul>
                <div className={styles.docCtaButton}>
                  <V2Link afterIcon="arrow_forward" href="#">
                    View Plans
                  </V2Link>
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Important Information */}
      {items.value === 'footnotes' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.body}>
              <div className={styles.footnote}>
                <div>
                  1 The amount shown does not include the Part B premium you
                  already pay to the government. Your plan costs, including
                  premiums and drug costs, may be different if you get Extra
                  Help from the government or if you have Medicaid. For more
                  information, see "Can I Get Help with my Medicare Prescription
                  Drug Costs?."
                </div>

                <div>
                  Benefits, premium and/or copayments/coinsurance may change on
                  January 1 of each year.
                </div>

                <div>
                  2 Amounts may vary depending on the level of care provided or
                  the type of health care services you receive.
                </div>

                <div>
                  3 Benefits, features and/or devices may vary by plan/area.
                  Limitations and exclusions apply. See the Summary of Benefits
                  (PDF) in the Plan Documents section for details.
                </div>

                <div>
                  4 Benefits, features and/or devices may vary by plan/area.
                  Limitations and exclusions apply. See the Evidence of Coverage
                  (PDF) in the Plan Documents section for details.
                </div>
              </div>

              <div className={styles.insuredNumberContainer}>
                <div className={styles.insuredNumber}>
                  <div className={styles.insuredNumberMedia}>
                    <img
                      src="/assets/images/insured-icon.png"
                      alt="insured icon"
                    />
                  </div>
                  <div className={styles.insuredNumberContent}>
                    <h3 className={styles.insuredNumberTitle}>
                      Already an insured member?
                    </h3>
                    <p>
                      For accurate information about rates and plans if you are
                      currently insured under an AARP Medicare Supplement Plan,
                      please call UnitedHealthcare for information.
                    </p>
                    <div className={styles.insuredNumberTel}>
                      <a href="tel:#">1-866-603-3424</a> TTY 711
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}

      {/* Not Coveered */}
      {items.value === 'not_covered' && (
        <React.Fragment>
          <div className={styles.section}>
            <div className={styles.body}>
              <div className={styles.footnote}>
                <div>1. Hospital Stays</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge if
                  you are confined for inpatient care in a Hospital and the stay
                  is not covered by Medicare. This Plan will pay for Hospital
                  room and board, and other services and supplies furnished by
                  the Hospital for medical care.
                </div>
                <div>This benefit has these added requirements</div>
                <div>
                  Benefits will be paid up to the maximum charge amount allowed
                  by law.
                </div>
                <div>
                  This Plan will not pay benefits for a private room if the
                  charge is more than the Hospital's charge for its most common
                  semi-private room, unless a private room is prescribed as
                  Medically Necessary by your Physician.
                </div>
                <div>2. Medical Care</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  the following Covered Expenses when the charges are not paid
                  by Medicare or otherwise covered under this Plan.
                </div>
                <div>This benefit has these added requirements</div>
                <div>
                  Benefits will be paid up to the maximum charge amount allowed
                  by law.
                </div>
                <div>
                  Payment of benefits will not duplicate benefits payable under
                  Medicare or any other coverage provided by this Plan.
                </div>
                <div>These Covered Expenses include the following:</div>
                <div>
                  A. Physician Services – Physician services for the diagnosis
                  and treatment of Sickness or Injury. This includes a second
                  opinion from a Physician on any surgical procedure expected to
                  cost at least $500 in Physician, laboratory and Hospital fees.
                </div>
                <div>
                  B. Nursing Home Services – Nursing Home services for the
                  following:
                </div>
                <div>
                  Nursing Home room and board for regular daily services and
                  supplies furnished by the Nursing Home. This Plan will not pay
                  benefits for a private room if the charge is more than the
                  Nursing Home's charge for its most common semi-private room,
                  unless a private room is prescribed as Medically Necessary by
                  your Physician. If the Nursing Home does not have semi-private
                  rooms, its most common semi-private room charge will be 90% of
                  its lowest private room charge.
                </div>
                <div>
                  All other services and supplies furnished by the Nursing Home
                  for medical care. Benefits will not be paid for more than 120
                  days in a calendar year. Your Nursing Horne stay must start
                  with in 30 days after you are Hospital confined for at least
                  three days in a row for the same or related Sickness or
                  Injury. You must need nursing care 24 hours a day to treat
                  your Sickness or Injury.
                </div>
                <div>
                  C. Therapy – Treatment by a physical therapist or occupational
                  therapist at the direction of your Physician.
                </div>
                <div>
                  D. Oral Surgery – Oral surgery for partially or completely
                  unerupted impacted teeth, a tooth root without extraction of
                  the entire tooth, or the gums and tissue of your mouth.
                  Benefits will not be paid for oral surgery performed in
                  connection with the extraction or repair of teeth. Benefits
                  will not be paid for root canal, gingivitis or periodontal
                  disease.
                </div>
                <div>
                  E. Ambulance – Ambulance transportation provided by a licensed
                  ambulance service to the nearest facility qualified to treat
                  your Sickness or Injury. This includes a reasonable mileage
                  rate for transportation to a kidney dialysis center for
                  treatment.
                </div>
                <div>
                  F. Other Services and Supplies – The following other services
                  and supplies:
                </div>
                <div>Use of radium or other radioactive materials</div>
                <div>Oxygen</div>
                <div>Anesthetics and their administration</div>
                <div>
                  Artificial limbs, eyes, larynx, and other prosthetic devices
                  (other than dental, but including scalp hair prosthesis worn
                  for hair loss suffered as a result of alopecia areata; see the
                  "Additional State Mandated Benefits" section for scalp hair
                  prosthesis benefit limitations)
                </div>
                <div>
                  Rental or purchase, as appropriate, of durable medical
                  equipment, other than eyeglasses and hearing aids
                </div>
                <div>Diagnostic x-ray exam and laboratory tests</div>
                <div>
                  G. Home Health Agency – Home Health Agency services for the
                  following:
                </div>
                <div>
                  Part-time or intermittent home nursing care by or under the
                  supervision of a Registered Nurse.
                </div>
                <div>
                  Part-time or intermittent home health aide services, primarily
                  for your care.
                </div>
                <div>
                  You must receive services in your home by a member of a Home
                  Health Agency. Services must be for the care and treatment of
                  your Sickness or Injury. If you could not receive these
                  services in your home, confinement in a Hospital or nursing
                  facility would be needed for you to receive the required care.
                  Each visit by a member of a Home Health Agency shall be
                  considered as one visit. Benefits will not be paid for
                  prescription drugs included in the home care plan of
                  treatment. For care received outside the United States, you
                  must receive home health services outside a hospital or
                  nursing home facility.
                </div>
                <div>
                  H. Emergency Services – Emergency services 24 hours a day and
                  seven days a week for the treatment of an Emergency Medical
                  Condition.
                </div>
                <div>3. Care received outside the United States</div>
                <div>
                  Medicare usually does not cover care you receive outside the
                  United States. In most cases, you are responsible for the full
                  cost of care you receive out side the United States.
                </div>
                <div>
                  This Plan covers certain care while you are on a Trip outside
                  the United States.
                </div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  the services described above as Hospital Stays and Medical
                  Care under the "Description of Benefits for Expenses Not
                  Covered by Medicare." This Plan will pay these benefits if the
                  services you received while in a foreign country would have
                  qualified as Medicare Eligible Expenses if they had been
                  received in the United States.
                </div>
                <div>This benefit has these added requirements</div>
                <div>
                  Benefits will be paid for a stay or care only if these three
                  things are true:
                </div>
                <div>
                  The service is not covered by Medicare, but would have been
                  covered by Medicare if it had been received in the United
                  States.
                </div>
                <div>
                  The service is received on or after your Effective Date while
                  you are in a foreign country.
                </div>
                <div>
                  The person providing medical care cannot be you or your
                  spouse; your or your spouse's child, brother, sister or
                  parent.
                </div>
                <div>The following exclusions apply to this benefit:</div>
                <div>
                  Benefits are not payable for charges in excess of any charge
                  limitation established by Medicare or Minnesota law.
                </div>
                <div>
                  If you establish residency outside the United States, we will
                  not pay benefits for any service or supply rendered in the
                  foreign country. You must provide us with an itemized bill for
                  your care. You must also provide us with any necessary and
                  relevant medical records that we request. We will pay these
                  benefits in United States currency. We will calculate the
                  amount of our payment using the bank transfer exchange rate in
                  effect on the day we process your claim for payment.
                </div>
                <div>
                  Payment of benefits will not duplicate benefits payable under
                  Medicare or any other coverage provided by this Plan.
                </div>
                <div>4. Preventive Medical Care</div>
                <div>
                  This Plan will pay benefits for the actual charges up to 100%
                  of the Medicare-approved amount for the following preventive
                  health services:
                </div>
                <div>
                  An annual clinical preventive medical history and physical
                  examination. This examination may include tests and services
                  from paragraph b. below and patient education to address
                  preventive health care measures.
                </div>
                <div>
                  Preventive screening tests and preventive services. The
                  selection and frequency of these tests and services must be
                  medically appropriate as determined by your Physician.
                </div>
                <div>
                  This Plan will pay benefits for these preventive health
                  services, as if Medicare were to cover the service as
                  identified in American Medical Association Current Procedural
                  Terminology (AMA CPT) codes. The preventive health service
                  must be received on or after the Effective Date. This benefit
                  has a limit of $120 each calendar year for all preventive
                  health services combined.
                </div>
                <div>
                  This benefit will not be paid for any preventive health
                  service that is:
                </div>
                <div>covered by Medicare.</div>
                <div>performed for the treatment of a Sickness or Injury.</div>
                <div>
                  used to diagnose or treat a condition that has already
                  manifested itself.
                </div>
                <div>5. Additional State-Mandated Benefits</div>
                <div>
                  This Plan will pay benefits for the following care and
                  services only if these two things are true:
                </div>
                <div>
                  The procedure must be deemed appropriate by your Physician.
                </div>
                <div>
                  The procedure must be received on or after your Effective
                  Date.
                </div>
                <div>
                  Payment of benefits will not duplicate benefits payable under
                  Medicare or any other coverage provided by this Plan.
                </div>
                <div>A. Alcoholism, Chemical Dependency, Drug Addiction</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  the treatment of alcoholism and chemical dependency on the
                  same basis as coverage for any other condition when treatment
                  is provided for: (1) outpatient chemical dependency and
                  alcoholism services that must not place a greater financial
                  burden on the Insured or be more restrictive than those
                  requirements and limitations for outpatient medical services;
                  (2) inpatient hospital and residential chemical dependency and
                  alcoholism services that must not place a greater financial
                  burden on the Insured or be more restrictive than those
                  requirements and limitations for inpatient hospital medical
                  services.
                </div>
                <div>B. Ambulatory Surgical Center Services</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  care provided in an Ambulatory Surgical Center.
                </div>
                <div>C. Court-ordered Mental Health Services</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge, when
                  ordered by a court of competent jurisdiction, for mental
                  health services issued on the basis of a behavioral care
                  evaluation performed by a licensed psychiatrist which includes
                  the diagnosis and an individual treatment plan for care in the
                  most appropriate, least restrictive environment. Coverage is
                  contingent on the evaluation and court-ordered treatment plan
                  being performed by a participating provider or another
                  provider as required by law.
                </div>
                <div>
                  D. Diabetes Equipment and Supplies; Diabetes Outpatient
                  Self-Management Training and Education
                </div>
                <div>
                  This Plan will pay 80% for all Physician-prescribed, Medically
                  Necessary diabetic equipment and supplies (includes oral and
                  injectable insulin) for diabetes self-management training and
                  self-education classes, medical nutrition therapy, and
                  treatment of diabetes not otherwise covered under Part D of
                  the Medicare program. The Plan coverage for diabetes
                  outpatient self-management training and education, including
                  medical nutrition therapy, is covered when provided by a
                  certified, registered, or licensed health care professional
                  working in a program consistent with the national standards of
                  diabetes self-management education as established by the
                  American Diabetes Association. Coverage includes persons with
                  gestational, Type I, or Type II diabetes, subject to the
                  Medicare Part B Deductible.
                </div>
                <div>E. Immunizations</div>
                <div>
                  This Plan will pay 100% of the cost of routine immunizations
                  that are not covered under Medicare. Immunizations include any
                  medically recognized vaccine intended to produce immunity
                  against a specified disease or condition.
                </div>
                <div>F. Lyme Disease Benefit</div>
                <div>
                  This Plan will pay for treatment of Lyme Disease to the same
                  extent that we pay for treatment of any other Sickness.
                </div>
                <div>G. Mental Health Services Benefit</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  outpatient mental health covered services that are intended to
                  treat or ameliorate an emotional, behavioral, or psychiatric
                  condition if they are Medically Necessary.
                </div>
                <div>H. Phenylketonuria Treatment</div>
                <div>
                  This Plan will pay 80% of Covered Expenses for special dietary
                  treatment for phenylketonuria when recommended by a Physician.
                </div>
                <div>I. Reconstructive Surgery</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  the following types of care:
                </div>
                <div>
                  Reconstructive surgery. The surgery must be incidental to or
                  following surgery for Injury, Sickness, or other diseases of
                  the involved part.
                </div>
                <div>
                  Reconstructive breast surgery following mastectomies if
                  mastectomy is Medically Necessary. This includes all stages of
                  reconstruction of the breast on which the mastectomy has been
                  performed, surgery, and reconstruction of the other breast to
                  produce a symmetrical appearance, and prosthesis and physical
                  complications at all stages of a mastectomy, including
                  lymphedemas.
                </div>
                <div>J. Routine Cancer Screening</div>
                <div>
                  This Plan will pay 100% of the cost of routine cancer
                  screening procedures that are not covered under Medicare.
                  Cancer screening procedures include:
                </div>
                <div>
                  Mammograms. Coverage for preventive mammogram screening that
                  includes digital breast tomosynthesis (3D) for women who are
                  at risk for breast cancer.
                </div>
                <div>
                  Surveillance tests for ovarian cancer for women who are at
                  risk for ovarian cancer (annual screening using CA-125 serum
                  tumor marker testing, transvaginal ultrasound, pelvic
                  examination, or other proven ovarian cancer screening tests
                  currently being evaluated by the federal Food and Drug
                  Administration or by the National Cancer Institute).
                </div>
                <div>
                  "At risk for ovarian cancer" means (1) having a family history
                  with one or more first or second degree relatives with ovarian
                  cancer, of clusters of women relatives with breast cancer, or
                  of nonpolyposis colorectal cancer, or (2) testing positive for
                  BRCA1 or BRCA2 mutations.
                </div>
                <div>Papanicolaou tests (pap smears)</div>
                <div>Colorectal screening tests</div>
                <div>
                  Prostate cancer screening consisting of a prostate-specific
                  antigen blood test and a digital rectal examination
                </div>
                <div>K. Scalp Hair Prosthesis</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for a
                  scalp hair prosthesis needed because of hair loss suffered as
                  a result of alopecia areata, This Plan has a limit of one (1)
                  scalp hair pros thesis per calendar year.
                </div>
                <div>
                  L. Temporomandibular Joint Disorder and Craniomandibular
                  Disorder
                </div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  the surgical and nonsurgical treatment of temporomandibular
                  joint disorder and craniomandibular disorder on the same basis
                  as that for treatment to any other joint in the body. Such
                  treatment must be administered or prescribed by a Physician or
                  dentist.
                </div>
                <div>M. Ventilator Dependent Benefit</div>
                <div>
                  This Plan will pay 80% of the Usual and Customary Charge for
                  services by a private-duty nurse or personal care assistant to
                  a ventilator-dependent person in the person's home. The Plan
                  pay the Usual and Customary Charge for services provided by a
                  private-duty nurse or personal care assistant to the
                  ventilator-dependent person during the time the
                  ventilator-dependent person is in a licensed hospital, not to
                  exceed 120 hours. The personal care assistant or private-duty
                  nurse shall perform only the services of communicator or
                  interpreter for the ventilator-dependent patient during a
                  transition period of up to 120 hours to assure adequate
                  training of the hospital staff to communicate with the patient
                  and to understand the unique comfort, safety, and personal
                  care needs of the ventilator-dependent patient.
                </div>
                <div>
                  6. Out-of-pocket expenses – After total out-of-pocket Covered
                  Expenses of $1,000 are paid in any calendar year by you, all
                  benefits not subject to an annual limit will be paid at 100%
                  of the Usual and Customary Charge for the rest of the calendar
                  year.
                </div>
              </div>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
